/**
 * OnboardingContent — Account hub tab showing onboarding completion rates
 * and per-user status for the current organization.
 *
 * Lives in SMS (the school management product), not AIM. Reached via
 * Account → Onboarding (was Admin → Onboarding before ADR-114).
 * ADR 085 Phase 5.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Text,
  Flex,
  Badge,
  Button,
  Table,
  Separator,
  Spinner,
} from '@chakra-ui/react';
import {
  HiUsers,
  HiCheckCircle,
  HiXCircle,
  HiClock,
  HiArrowPath,
} from 'react-icons/hi2';
import {
  useOnboardingAnalytics,
  useOnboardingUsers,
} from '../../hooks/queries/useOnboarding';
import type { UserStatusFilters } from '../../services/onboarding.service';

function StatCard({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
}) {
  return (
    <Box
      p={4}
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      flex={1}
      minW="140px"
      data-e2e={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Flex alignItems="center" gap={2} mb={1}>
        <Box color={color} fontSize="lg">
          {icon}
        </Box>
        <Text fontSize="2xl" fontWeight="bold">
          {value}
        </Text>
      </Flex>
      <Text fontSize="xs" color="gray.500">
        {label}
      </Text>
    </Box>
  );
}

export function OnboardingContent() {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(1);

  const statusBadgeMap: Record<string, { color: string; label: string }> = {
    completed: { color: 'green', label: t('onboarding.completed', 'Completed') },
    skipped: { color: 'orange', label: t('onboarding.skipped', 'Skipped') },
    in_progress: { color: 'blue', label: t('onboarding.inProgress', 'In Progress') },
    not_started: { color: 'gray', label: t('onboarding.notStarted', 'Not Started') },
  };

  const stepLabelsMap: Record<string, string> = {
    sign_in: t('onboarding.signInStep', 'Sign In'),
    profile: t('onboarding.profileStep', 'Profile'),
    default_location: t('onboarding.locationStep', 'Location'),
    guided_tour: t('onboarding.tourStep', 'Tour'),
    notifications: t('onboarding.notificationsStep', 'Notifications'),
  };

  const { data: analytics, isLoading: analyticsLoading } =
    useOnboardingAnalytics();

  const userFilters: UserStatusFilters = {
    status: statusFilter,
    search: searchValue || undefined,
    page,
    page_size: 25,
  };
  const { data: users, isLoading: usersLoading } =
    useOnboardingUsers(userFilters);

  if (analyticsLoading) {
    return (
      <Flex p={8} justifyContent="center" data-e2e="onboarding-loading">
        <Spinner />
      </Flex>
    );
  }

  const summary = analytics?.onboarding_summary;

  return (
    <Box px={5} pt={4} pb={2} data-e2e="onboarding-content">
      {/* Summary cards */}
      <Flex gap={3} mb={6} flexWrap="wrap" data-e2e="summary-cards">
        <StatCard
          label={t('onboarding.totalUsers', 'Total Users')}
          value={analytics?.total_users ?? 0}
          icon={<HiUsers />}
          color="gray.600"
        />
        <StatCard
          label={t('onboarding.completed', 'Completed')}
          value={summary?.completed ?? 0}
          icon={<HiCheckCircle />}
          color="green.500"
        />
        <StatCard
          label={t('onboarding.inProgress', 'In Progress')}
          value={summary?.in_progress ?? 0}
          icon={<HiClock />}
          color="blue.500"
        />
        <StatCard
          label={t('onboarding.notStarted', 'Not Started')}
          value={summary?.not_started ?? 0}
          icon={<HiXCircle />}
          color="gray.400"
        />
        <StatCard
          label={t('onboarding.completionRate', 'Completion Rate')}
          value={`${summary?.completion_rate ?? 0}%`}
          icon={<HiArrowPath />}
          color="teal.500"
        />
      </Flex>

      {/* Per-step completion rates */}
      <Text fontWeight="semibold" fontSize="md" mb={2} data-e2e="step-completion-heading">
        {t('onboarding.stepCompletionRates', 'Step Completion Rates')}
      </Text>
      <Flex gap={2} mb={6} flexWrap="wrap" data-e2e="step-completion-rates">
        {analytics?.step_stats &&
          Object.entries(analytics.step_stats).map(([step, stats]) => (
            <Box
              key={step}
              p={3}
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              flex={1}
              minW="120px"
              data-e2e={`step-stat-${step}`}
            >
              <Text fontSize="xs" fontWeight="medium" mb={1} data-e2e={`step-stat-${step}-label`}>
                {stepLabelsMap[step] || step}
              </Text>
              <Text fontSize="lg" fontWeight="bold" color="teal.600" data-e2e={`step-stat-${step}-completion`}>
                {stats.completion_rate}%
              </Text>
              <Text fontSize="xs" color="gray.400" data-e2e={`step-stat-${step}-info`}>
                {t('onboarding.doneSkippedInfo', '{{completed}} done, {{skipped}} skipped', { completed: stats.completed, skipped: stats.skipped })}
              </Text>
            </Box>
          ))}
      </Flex>

      {/* Troubleshooting stats */}
      {analytics?.platform_troubleshooting &&
        analytics.platform_troubleshooting.length > 0 && (
          <>
            <Text fontWeight="semibold" fontSize="md" mb={2} data-e2e="troubleshooting-heading">
              {t('onboarding.aiTroubleshootingByPlatform', 'AI Troubleshooting by Platform')}
            </Text>
            <Table.Root size="sm" mb={6} data-e2e="troubleshooting-table">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>{t('onboarding.colPlatform', 'Platform')}</Table.ColumnHeader>
                  <Table.ColumnHeader>{t('onboarding.colSessions', 'Sessions')}</Table.ColumnHeader>
                  <Table.ColumnHeader>{t('onboarding.colResolved', 'Resolved')}</Table.ColumnHeader>
                  <Table.ColumnHeader>{t('onboarding.colEscalated', 'Escalated')}</Table.ColumnHeader>
                  <Table.ColumnHeader>{t('onboarding.colAvgTurns', 'Avg Turns')}</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {analytics.platform_troubleshooting.map((p) => (
                  <Table.Row key={p.platform} data-e2e={`troubleshooting-row-${p.platform}`}>
                    <Table.Cell>{p.platform}</Table.Cell>
                    <Table.Cell>{p.sessions}</Table.Cell>
                    <Table.Cell>{p.resolved}</Table.Cell>
                    <Table.Cell>{p.escalated}</Table.Cell>
                    <Table.Cell>{p.avg_turns?.toFixed(1) ?? '-'}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </>
        )}

      {/* Re-engagement stats */}
      {analytics?.reengagement && analytics.reengagement.length > 0 && (
        <>
          <Text fontWeight="semibold" fontSize="md" mb={2} data-e2e="reengagement-heading">
            {t('onboarding.reengagementPrompts', 'Re-engagement Prompts')}
          </Text>
          <Flex gap={3} mb={6} data-e2e="reengagement-section">
            {analytics.reengagement.map((r) => (
              <Box
                key={r.event_type}
                p={3}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                data-e2e={`reengagement-card-${r.event_type}`}
              >
                <Text fontSize="xs" fontWeight="medium" mb={1} data-e2e={`reengagement-card-${r.event_type}-label`}>
                  {r.event_type === 'post_drill'
                    ? t('onboarding.postDrill', 'Post-Drill')
                    : t('onboarding.contextual', 'Contextual')}
                </Text>
                <Text fontSize="sm" data-e2e={`reengagement-card-${r.event_type}-info`}>
                  {t('onboarding.sentConvertedInfo', '{{sent}} sent, {{converted}} converted', {
                    sent: r.sent,
                    converted: r.converted,
                  })}{' '}
                  (
                  {r.sent > 0 ? Math.round((r.converted / r.sent) * 100) : 0}
                  %)
                </Text>
              </Box>
            ))}
          </Flex>
        </>
      )}

      <Separator mb={4} data-e2e="onboarding-separator" />

      {/* Per-user status table */}
      <Flex justifyContent="space-between" alignItems="center" mb={3} data-e2e="user-status-header">
        <Text fontWeight="semibold" fontSize="md" data-e2e="user-status-heading">
          {t('onboarding.userOnboardingStatus', 'User Onboarding Status')}
        </Text>
        <Flex gap={1} data-e2e="status-filter-group">
          {['all', 'completed', 'in_progress', 'not_started', 'skipped'].map(
            (filter) => {
              const isActive =
                filter === 'all' ? !statusFilter : statusFilter === filter;
              return (
                <Button
                  key={filter}
                  size="xs"
                  variant={isActive ? 'solid' : 'outline'}
                  colorPalette={isActive ? 'teal' : 'gray'}
                  onClick={() => {
                    setStatusFilter(filter === 'all' ? undefined : filter);
                    setPage(1);
                  }}
                  data-e2e={`${filter}-button`}
                >
                  {filter === 'all'
                    ? t('common.all', 'All')
                    : statusBadgeMap[filter]?.label || filter}
                </Button>
              );
            },
          )}
        </Flex>
      </Flex>

      {usersLoading ? (
        <Flex justifyContent="center" p={4} data-e2e="users-loading">
          <Spinner size="sm" />
        </Flex>
      ) : (
        <>
          <Table.Root size="sm" data-e2e="users-table">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>{t('common.name', 'Name')}</Table.ColumnHeader>
                <Table.ColumnHeader>{t('common.email', 'Email')}</Table.ColumnHeader>
                <Table.ColumnHeader>{t('common.status', 'Status')}</Table.ColumnHeader>
                <Table.ColumnHeader>{t('onboarding.signInStep', 'Sign In')}</Table.ColumnHeader>
                <Table.ColumnHeader>{t('onboarding.profileStep', 'Profile')}</Table.ColumnHeader>
                <Table.ColumnHeader>{t('onboarding.locationStep', 'Location')}</Table.ColumnHeader>
                <Table.ColumnHeader>{t('onboarding.tourStep', 'Tour')}</Table.ColumnHeader>
                <Table.ColumnHeader>{t('onboarding.notificationsStep', 'Notifications')}</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {users?.results?.map((user) => {
                const badge = statusBadgeMap[user.status];
                return (
                  <Table.Row key={user.id} data-e2e={`user-row-${user.id}`}>
                    <Table.Cell>{user.name}</Table.Cell>
                    <Table.Cell fontSize="xs">{user.email}</Table.Cell>
                    <Table.Cell>
                      <Badge colorPalette={badge?.color} size="sm" data-e2e={`user-status-badge-row-${user.id}`}>
                        {badge?.label}
                      </Badge>
                    </Table.Cell>
                    {[
                      'sign_in',
                      'profile',
                      'default_location',
                      'guided_tour',
                      'notifications',
                    ].map((stepId) => {
                      const step = user.steps?.find((s) => s.id === stepId);
                      const stepStatus = step?.status || 'pending';
                      return (
                        <Table.Cell key={stepId} data-e2e={`user-step-row-${user.id}-${stepId}`}>
                          <Badge
                            colorPalette={
                              stepStatus === 'completed'
                                ? 'green'
                                : stepStatus === 'skipped'
                                  ? 'orange'
                                  : 'gray'
                            }
                            size="sm"
                            variant="subtle"
                          >
                            {stepStatus === 'completed'
                              ? '✓'
                              : stepStatus === 'skipped'
                                ? '—'
                                : '·'}
                          </Badge>
                        </Table.Cell>
                      );
                    })}
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table.Root>

          {/* Pagination */}
          {users && users.total > 25 && (
            <Flex justifyContent="center" gap={2} mt={3} data-e2e="pagination">
              <Button
                size="xs"
                variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                data-e2e="previous-button"
              >
                {t('common.previous', 'Previous')}
              </Button>
              <Text fontSize="xs" color="gray.500" lineHeight="2" data-e2e="pagination-info">
                {t('common.paginationPage', 'Page {{current}} of {{total}}', { current: page, total: Math.ceil(users.total / 25) })}
              </Text>
              <Button
                size="xs"
                variant="outline"
                disabled={page >= Math.ceil(users.total / 25)}
                data-e2e="next-button"
                onClick={() => setPage((p) => p + 1)}
              >
                {t('common.next', 'Next')}
              </Button>
            </Flex>
          )}
        </>
      )}
    </Box>
  );
}
